import { createSlice, PayloadAction, UnknownAction } from '@reduxjs/toolkit'

import history from '@/config/router/history'
import { PATHS } from '@/config/router/routes'
import { startAppListening } from '@/config/store/listenerMiddleware'
import { DENIED_STORE_STATUSES } from '@/constants/common'
import { sendLoginDeniedEvent } from '@/ducks/app/helpers'
// Removed circular dependency - selectors will be accessed directly from state
import { startInitialization } from '@/ducks/app/slices/app.shared'
import { selectToken } from '@/ducks/auth/auth.selectors'
import {
  createAutoAcceptChimeSound,
  createNotificationSound,
  playAutoAcceptChime,
  stopAutoAcceptChime
} from '@/ducks/orders/utils/orders.utils'
import { fetchMyRoles } from '@/ducks/auth/auth.shared'
import storeApi from '@/ducks/store/api/store.api'
import {
  selectStoreAvailability,
  selectStoreId
} from '@/ducks/store/selectors/store.selectors'
import {
  setLoginDeniedStatus,
  setStoreProfileData
} from '@/ducks/store/slice/store.slice'
import {
  ApiGetStoreAvailability,
  IStoreProfileStatuses
} from '@/ducks/store/store.types'
import {
  readFromLocalStorage,
  setToLocalStorage
} from '@/services/localStorage'

const DEFAULT_APP_SETTINGS = {
  confirmMenuHideAction: true
}

export type BootStatus = 'idle' | 'loading' | 'ready' | 'failed'

export interface AppState {
  isInitialized: boolean
  bootStatus: BootStatus
  isSidebarOpen: boolean
  settings: {
    confirmMenuHideAction: boolean
  }
  flags: Record<string, unknown>
  /** False until LaunchDarkly's flags have reached the store at least once. */
  flagsReady: boolean
  notificationAppId: string | null
  notificationSound: HTMLAudioElement
  autoAcceptChimeSound: HTMLAudioElement
  pendingOrderIds: string[]
  hasUserInteracted: boolean
}

const initialState: AppState = {
  isInitialized: false,
  bootStatus: 'idle',
  isSidebarOpen: false,
  settings: { ...DEFAULT_APP_SETTINGS, ...readFromLocalStorage('settings') },
  flags: {},
  flagsReady: false,
  notificationAppId: null,
  notificationSound: createNotificationSound(),
  autoAcceptChimeSound: createAutoAcceptChimeSound(),
  pendingOrderIds: [],
  hasUserInteracted: false
}

const SLICE_NAME = 'app'

export const appSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    initializeApp: state => {
      state.isInitialized = true
      state.bootStatus = 'ready'
    },
    bootStarted: state => {
      state.bootStatus = 'loading'
    },
    bootFailed: state => {
      state.bootStatus = 'failed'
    },
    updateSettings: (
      state,
      action: PayloadAction<Partial<AppState['settings']>>
    ) => {
      state.settings = { ...state.settings, ...action.payload }
    },
    setSideBarOpen: (state, action: PayloadAction<boolean>) => {
      state.isSidebarOpen = action.payload
    },
    updateFlags: (state, action: PayloadAction<Partial<AppState['flags']>>) => {
      state.flags = action.payload
      state.flagsReady = true
    },
    setNotificationAppId: (
      state,
      action: PayloadAction<{ id: string | null }>
    ) => {
      state.notificationAppId = action.payload.id
    },
    stopAudioNotification: state => {
      state.notificationSound?.pause()

      if (state.notificationSound?.currentTime) {
        state.notificationSound.currentTime = 0
      }
    },
    playAudioNotification: state => {
      state.notificationSound?.play()
    },
    stopAutoAcceptChimeNotification: state => {
      if (state.autoAcceptChimeSound) {
        stopAutoAcceptChime(state.autoAcceptChimeSound as HTMLAudioElement)
      }
    },
    playAutoAcceptChimeNotification: (
      state,
      action: PayloadAction<{ orderId: string } | undefined>
    ) => {
      if (state.autoAcceptChimeSound) {
        playAutoAcceptChime(
          state.autoAcceptChimeSound as HTMLAudioElement,
          3,
          action.payload?.orderId
        )
      }
    },
    addOrderIdToPending: (
      state,
      action: PayloadAction<{ orderId: string }>
    ) => {
      state.pendingOrderIds.push(action.payload.orderId)
    },
    removeOrderIdFromPending: (
      state,
      action: PayloadAction<{ orderId: string }>
    ) => {
      state.pendingOrderIds = state.pendingOrderIds.filter(
        orderId => orderId !== action.payload.orderId
      )
    },
    setUserInteracted: state => {
      state.hasUserInteracted = true
    }
  }
})

startAppListening({
  predicate: (_: UnknownAction, currentState, previousState) => {
    const currentPendingOrders = currentState.app?.pendingOrderIds || []
    const previousPendingOrders = previousState.app?.pendingOrderIds || []
    return currentPendingOrders.length === 0 && previousPendingOrders.length > 0
  },
  effect: async function stopNotificationSound(_: UnknownAction, listenerApi) {
    listenerApi.dispatch(stopAudioNotification())
  }
})

startAppListening({
  predicate: (_: UnknownAction, currentState, previousState) => {
    const currentSettings = currentState.app?.settings
    const previousSettings = previousState.app?.settings
    return currentSettings !== previousSettings
  },
  effect: async function syncSettingsToLocalStorage(
    _: UnknownAction,
    listenerApi
  ) {
    const state = listenerApi.getState()
    const settings = state.app?.settings
    if (settings) {
      setToLocalStorage({ settings })
    }
  }
})

startAppListening({
  actionCreator: startInitialization,
  effect: async (_, listenerApi) => {
    // Boot is dispatched from both the Initializer mount and the login
    // listener; without this the whole chain runs twice on every cold boot.
    listenerApi.cancelActiveListeners()

    const token = selectToken(listenerApi.getState())
    const storeId = selectStoreId(listenerApi.getState())

    if (!token || !storeId) {
      // Not signed in: nothing to boot, let the public routes render.
      listenerApi.dispatch(initializeApp())
      return
    }

    listenerApi.dispatch(bootStarted())

    // Roles drive RBAC, not the shell: fetched alongside the boot rather than
    // inside it, so a slow or failed /me/roles never holds up the app and a
    // late-arriving flag still triggers a fetch (see auth.slice listeners).
    listenerApi.dispatch(fetchMyRoles())

    let storeInfo
    try {
      storeInfo = await listenerApi.pause(
        listenerApi
          .dispatch(
            storeApi.endpoints.fetchStoreProfile.initiate(
              { store_id: storeId },
              // forceRefetch so a retry after a boot failure really re-requests
              // instead of resolving against the failed cache entry.
              { subscribe: false, forceRefetch: true }
            )
          )
          .unwrap()
      )
    } catch (error) {
      if (listenerApi.signal.aborted) return
      // The profile carries the store status and identity: without it the
      // shell has nothing to render, so surface a retryable boot failure
      // instead of hiding the splash over an empty app.
      listenerApi.dispatch(bootFailed())
      return
    }

    const storeStatus = storeInfo?.profileData?.status as IStoreProfileStatuses
    listenerApi.dispatch(setLoginDeniedStatus({ status: storeStatus }))

    if (DENIED_STORE_STATUSES.includes(storeStatus)) {
      sendLoginDeniedEvent(storeStatus, storeId)

      history.push(PATHS.LOGIN_DENIED)
      listenerApi.dispatch(initializeApp())
      return
    }

    const { storeName, avatar, phoneNumber, phoneCode } = {
      avatar: storeInfo.profileData.store_img_profile,
      storeName: storeInfo.profileData.store_name,
      phoneNumber: storeInfo.profileData.phone?.phone1?.number,
      phoneCode: storeInfo.profileData.phone?.phone1?.country_code
    }

    // Availability is not boot-critical: fall back to the persisted value and
    // let the sidebar toggle refresh it rather than blocking the whole app.
    let availability = selectStoreAvailability(listenerApi.getState()) ?? 0
    try {
      const response: ApiGetStoreAvailability = await listenerApi.pause(
        listenerApi
          .dispatch(
            storeApi.endpoints.fetchStoreAvailability.initiate(
              { store_id: storeId },
              { subscribe: false, forceRefetch: true }
            )
          )
          .unwrap()
      )
      availability = response.availability
    } catch (error) {
      if (listenerApi.signal.aborted) return
    }

    listenerApi.dispatch(
      setStoreProfileData({
        availability,
        storeName,
        avatar,
        phoneNumber,
        phoneCode
      })
    )

    listenerApi.dispatch(
      storeApi.endpoints.getStoreAutoAcceptStatus.initiate(undefined, {
        subscribe: false
      })
    )

    if ([PATHS.LOGIN].includes(history.location.pathname)) {
      history.push(PATHS.ORDERS)
    }

    listenerApi.dispatch(initializeApp())
  }
})

export const {
  updateFlags,
  initializeApp,
  bootStarted,
  bootFailed,
  updateSettings,
  setSideBarOpen,
  setNotificationAppId,
  playAudioNotification,
  stopAudioNotification,
  playAutoAcceptChimeNotification,
  stopAutoAcceptChimeNotification,
  addOrderIdToPending,
  removeOrderIdFromPending,
  setUserInteracted
} = appSlice.actions
export default appSlice.reducer
